import { useEffect, useState } from "react";
import { useCallTimer } from "@/stores/callTimer.store";

/**
 * Subscribes to the call-timer store + a 1-second interval so the
 * caller component re-renders once per second while a call is
 * active. Returns 0 when no call is running.
 *
 * Kept in its own file (rather than colocated with the store) so
 * the per-second re-render only hits surfaces that actually
 * display the elapsed time — the rest of the app reads `active`
 * via the store hook without ticking.
 */
export function useCallElapsedSeconds(): number {
  const active = useCallTimer((s) => s.active);
  const startedAtMs = useCallTimer((s) => s.startedAtMs);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active || startedAtMs == null) {
      setElapsed(0);
      return;
    }
    const tick = () =>
      setElapsed(Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [active, startedAtMs]);

  return elapsed;
}
